import { uniqBy } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import { ADV_SEARCH_RESULTS_URL } from '@/constants/urls';
import {
  OpenSearchParcel,
  searchForParcels,
} from '@/endpoints/itasca/parcels/search';
import { useAuth } from '@/providers/auth-provider';

import {
  SearchResult,
  SearchResultGeometry,
  SearchResultType,
} from '../models';

export const useParcelOwnerAddressSearch = (search: string) => {
  const { userCanAdvancedSearchOwnerAddress } = useAuth();
  const [parcelOwnerAddresses, setParcelOwnerAddresses] = useState<
    SearchResult<OpenSearchParcel>[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [parcelOwnerAddressesSeeMore, setParcelOwnerAddressesSeeMore] =
    useState<number>(0);

  useEffect(() => {
    setParcelOwnerAddresses([]);
    setParcelOwnerAddressesSeeMore(0);
  }, [search]);

  const fetchParcelOwners = useCallback(
    async (signal: AbortSignal) => {
      if (!search) return;

      if (!userCanAdvancedSearchOwnerAddress) return;

      setLoading(true);
      try {
        const { res, err } = await searchForParcels({
          data: {
            filters: [],
            // Requesting > 5 here hoping to get at least 5 after deduping results
            limit: 10,
            offset: 0,
            queries: [{ mailing_address: search }],
          },
          signal,
        });
        if (err) {
          throw err;
        }

        if (res) {
          const dedupeResults: OpenSearchParcel[] = uniqBy(
            res.data.results,
            'mailing_address'
          );

          setParcelOwnerAddresses(
            dedupeResults.splice(0, 5).map((x) => ({
              geometry: x.geometry as SearchResultGeometry,
              id: `parcel-owner-address-${x.apn}`,
              label: `${x.mailing_address}`,
              original: x,
              type: SearchResultType.PARCEL_OWNER_ADDRESS,
              url: `${ADV_SEARCH_RESULTS_URL}?search=true&mailing_address=${x.mailing_address}`,
            }))
          );
          setParcelOwnerAddressesSeeMore(res.data.total_hits ?? 0);
        }
      } catch (err) {
        return;
      } finally {
        setLoading(false);
      }
    },
    [search, userCanAdvancedSearchOwnerAddress]
  );

  useEffect(() => {
    const abortController = new AbortController();
    fetchParcelOwners(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchParcelOwners]);

  return {
    loading,
    parcelOwnerAddresses,
    parcelOwnerAddressesSeeMore,
  };
};
